//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary, lighten } from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import ProfileDetailInfo from './ProfileDetailInfo';
import ProfileDetailSummary from './ProfileDetailSummary';
import { FormJob, FormValues } from '@/redux/slices/slice_types';
import { useAppDispatch } from '@/redux/hooks';
import { setSelectedHierarchy } from '@/redux/slices/orderPlacementSlice';

type ProfileDetailsProps = {
  lastIsOpen: boolean;
};

const ProfileDetails = ({ lastIsOpen }: ProfileDetailsProps) => {
  const { values, setValues } = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<number | string>('');

  const workflow_type = values.jobs?.[0]?.profile?.data?.workflow_type || 'version_based';
  const isMagicWand = ['version_based', 'package', 'version_based_bundle'].indexOf(workflow_type) !== -1;

  const toggleAccordion = (index: typeof expanded) => {
    setExpanded(expanded === index ? '' : index);
  };

  const deleteProfile = (values: FormValues, index: number) => {
    setValues({
      ...values,
      jobs: [...values.jobs.filter((_: FormJob, i: number) => i !== index)]
    });
  };

  useEffect(() => {
    if (values.jobs[0]?.profile?.data?.template_inputs?.hierarchy || values.jobs[0]?.profile?.data?.template_inputs?.business_unit) {
      dispatch(
        setSelectedHierarchy(
          values.jobs[0]?.profile?.data?.template_inputs?.hierarchy || values.jobs[0]?.profile?.data?.template_inputs?.business_unit
        )
      );
    } else {
      dispatch(setSelectedHierarchy(''));
    }
  }, [values.jobs]);

  return (
    <>
      {values?.jobs?.map((formJob: FormJob, i: number) => (
        <div key={i}>
          {(lastIsOpen && i === values?.jobs?.length - 1) || (values?.jobs?.length === 1 && !isMagicWand) ? (
            <ProfileDetailInfo job={formJob} index={i} showDelete hasMultipleJobs={values.jobs.length > 1} />
          ) : (
            <ProfileAccordion expanded={+(expanded === i)}>
              <Accordion disableGutters expanded={expanded === i} sx={{ mb: 2 }}>
                <StyledAccordionSummary
                  aria-controls="profile-detail"
                  onClick={() => toggleAccordion(i)}
                  expandIcon={<ExpandIcon width={25} icon="right" />}
                >
                  <ProfileDetailSummary currentJob={values.jobs[i]} deleteProfile={() => deleteProfile(values, i)} />
                </StyledAccordionSummary>
                <StyledAccordionDetails sx={{ p: 0 }}>
                  <ProfileDetailInfo job={formJob} index={i} hasMultipleJobs={values.jobs.length > 1} />
                </StyledAccordionDetails>
              </Accordion>
            </ProfileAccordion>
          )}
        </div>
      ))}
    </>
  );
};

export default ProfileDetails;

const StyledAccordionSummary = styled(AccordionSummary)`
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.07)};
  ${({ theme }) => theme.customOverrides?.AddedJobAccordion}
`;

const ProfileAccordion = styled.div<{ expanded: number }>`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  background: none;

  .MuiPaper-root {
    overflow: hidden;

    .MuiAccordionSummary-root {
      padding: ${({ theme }) => theme.spacing(2)};
    }

    .MuiAccordionDetails-root {
      padding: 0;

      .MuiPaper-root {
        border-radius: 0;
        border-right: 0;
        border-left: 0;
        border-bottom: 0;
      }
    }
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  .output-container {
    border-top-right-radius: 0;
  }
`;

const ExpandIcon = styled(PolarisIcon)`
  cursor: pointer;
  opacity: 0.5;
  transition: ease opacity 300ms;
  margin: none;

  &:hover {
    opacity: 1;
    transition: ease opacity 300ms;
  }
`;
